import { useField } from 'formik';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { AutoComplete } from '../AutoComplete';
import { AutoCompleteKey } from '../AutoCompleteKey';
import { getAutoCompleteConfig } from '../AutoCompleteService';
import { AutoCompleteItem } from '../AutoCompleteTypes';

interface FormikAutoCompleteProps {
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  autocompleteKey: AutoCompleteKey;
  noResultsText?: string;
  /**
   * Label of the value the field already holds, when the caller knows it (an
   * entity being edited usually carries it alongside the id). Given it, the
   * selected item is displayed without any lookup — which is the only reliable
   * way when the value is not on the first page of results.
   */
  initialLabel?: string | null;
}

export const FormikAutoComplete = ({
  name,
  label,
  placeholder,
  required,
  disabled,
  autocompleteKey,
  noResultsText,
  initialLabel,
}: FormikAutoCompleteProps) => {
  const [field, meta, helpers] = useField(name);
  const [items, setItems] = useState<AutoCompleteItem[]>([]);
  /* The selected item lives in its own state instead of being looked up in the
     current result page: searching again replaces the page, and deriving the
     selection from it blanked the field the user had already chosen. */
  const [selectedItem, setSelectedItem] = useState<AutoCompleteItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const resolvedIdRef = useRef<AutoCompleteItem['id'] | null>(null);

  const config = getAutoCompleteConfig(autocompleteKey);
  const fieldValue = field.value;

  const handleSearch = useCallback(
    async (query: string) => {
      setIsLoading(true);
      try {
        const response = await config.fetchFn({
          filter: query,
          maxResultCount: config.maxResults || 10,
        });
        setItems(response.items.map(config.mapItem));
      } catch (error) {
        console.error('AutoComplete search error:', error);
        setItems([]);
      } finally {
        setIsLoading(false);
      }
    },
    [config]
  );

  useEffect(() => {
    if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
      resolvedIdRef.current = null;
      // A user clearing the field already reset the selection in handleChange; an
      // empty value seen while the caller knows the current label must not wipe it.
      if (!initialLabel) {
        setSelectedItem(null);
      }
      return;
    }

    // Already resolved (or just picked by the user): nothing to look up.
    if (resolvedIdRef.current === fieldValue) {
      return;
    }
    resolvedIdRef.current = fieldValue;

    if (initialLabel) {
      setSelectedItem({ id: fieldValue, label: initialLabel });
      return;
    }

    // Best effort without a known label: the value may simply not be on the
    // first page, in which case the field stays empty rather than showing a
    // wrong label.
    let cancelled = false;
    const resolveInitialValue = async () => {
      setIsLoading(true);
      try {
        const response = await config.fetchFn({
          filter: '',
          maxResultCount: config.maxResults || 10,
        });
        const matchingItem = response.items
          .map(config.mapItem)
          .find((item) => item.id === fieldValue);
        if (!cancelled && matchingItem) {
          setSelectedItem(matchingItem);
        }
      } catch (error) {
        console.error('Error loading initial autocomplete value:', error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    resolveInitialValue();
    return () => {
      cancelled = true;
    };
  }, [fieldValue, initialLabel, config]);

  const handleChange = useCallback((item: AutoCompleteItem | null) => {
    resolvedIdRef.current = item?.id ?? null;
    setSelectedItem(item);
    helpers.setValue(item?.id || null);
  }, [helpers]);

  const error = useMemo(() => meta.touched && meta.error ? meta.error : undefined, [meta.error, meta.touched]);

  return (
    <AutoComplete
      label={label}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      value={selectedItem}
      onChange={handleChange}
      onSearch={handleSearch}
      items={items}
      isLoading={isLoading}
      error={error}
      noResultsText={noResultsText}
      minChars={config.minChars}
    />
  );
};
